import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Phone, CheckCircle, MessageSquare } from 'lucide-react';
import { publicApi } from '../services/api';
import { companyOf } from '../utils/company';
import AddressAutocomplete, { AddressSuggestion } from '../components/AddressAutocomplete';
import { PublicPage, PublicLoading, DeadLink, Panel, H1, ButtonPrimary, ButtonGhost } from '../components/public';

/**
 * Where a shared referral link lands (/r/CODE). Public, no login.
 *
 * Owner, Sep 18 2026: "I want this to be a form the customer can fill out."
 * It says who sent them and what they get, then asks the same short set of
 * questions as everywhere else — first, last, mobile, the yard, what they
 * want, the best time to reach them — and sends it through the same door the
 * website form uses (/api/leads/public) with the code attached.
 *
 * Rebuilt Sep 19 2026 on the shared customer skin. The questions and the
 * plumbing are the same; the labels are 14px instead of 11px, every control is
 * at least 48px tall, and the reward is stated once, plainly, at the top.
 */

const WANTS = [
  { key: 'mowing', label: 'Mowing' },
  { key: 'plan', label: 'Lawn plan' },
  { key: 'project', label: 'A project' },
  { key: 'unsure', label: 'Not sure yet' },
];
const WINDOWS = [
  { key: 'morning', label: 'Morning' },
  { key: 'midday', label: 'Midday' },
  { key: 'afternoon', label: 'Afternoon' },
  { key: 'evening', label: 'Evening' },
];
// The day they'd like — asked with the window so booking needs no follow-up text.
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const digits = (s: string) => s.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1');

const FIELD = 'w-full border-2 border-brand-200 rounded-2xl px-4 min-h-[54px] text-[17px] bg-white text-gray-900 placeholder-gray-500 shadow-soft focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100';
const LABEL = 'block text-[14px] font-bold text-gray-900 mb-1';

export default function ReferralLanding() {
  const { code } = useParams<{ code: string }>();
  const { data, isLoading, isError } = useQuery(['referral', code], () => publicApi.get(`/portal/referral/${code}`).then(r => r.data), { enabled: !!code, retry: false });
  const co = companyOf(data);
  const first = data?.referrer_first_name || 'A neighbor';
  const clean = data?.code || String(code || '').toUpperCase();

  const [f, setF] = useState({ first_name: '', last_name: '', phone: '', address: '', city: '', state: 'TX', zip: '', wants: '', window: '', pday: '' });
  // Real free times for a quote visit (his windows, minus the app's calendar and his phone's).
  const { data: slots, refetch: refetchSlots } = useQuery('quote-slots', () => publicApi.get('/portal/quote-slots').then(r => r.data), { staleTime: 60_000, retry: false });
  const [day, setDay] = useState<string>('');
  const [time, setTime] = useState<string>('');
  const [justTime, setJustTime] = useState(false);   // "just tell us the best time" instead of a slot
  const dayRow = (slots?.days || []).find((d: any) => d.date === day) || null;
  const [picked, setPicked] = useState<AddressSuggestion | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<any>(null);
  const [leadSaved, setLeadSaved] = useState<any>(null);

  async function bookAgain() {
    if (!leadSaved || !day || !time) return;
    setSending(true); setProblem(null);
    try {
      const b = await publicApi.post('/portal/quote-visit', { lead_id: leadSaved.lead_id, book_token: leadSaved.book_token, date: day, time });
      setSent({ ...leadSaved, booked: b.data });
    } catch (e: any) {
      if (e?.response?.data?.taken) { setProblem('That one went too. Pick another.'); setTime(''); refetchSlots(); }
      else setSent({ ...leadSaved, booked: null, bookFailed: e?.response?.data?.message || 'could not book the time' });
    } finally { setSending(false); }
  }
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF(p => ({ ...p, [k]: e.target.value }));
  const chip = (on: boolean) =>
    `press px-5 min-h-[50px] rounded-full border-2 text-[15px] font-bold ${on ? 'bg-brand-600 text-black border-brand-600 shadow-brand' : 'bg-white text-gray-900 border-brand-200 shadow-soft'}`;

  async function send() {
    setProblem(null);
    if (!f.first_name.trim()) { setProblem('Your first name is all we need to start.'); return; }
    if (digits(f.phone).length !== 10) { setProblem('A 10-digit mobile number, like 512-555-0100, so we can text you.'); return; }
    setSending(true);
    try {
      const r = await publicApi.post('/leads/public', {
        first_name: f.first_name.trim(), last_name: f.last_name.trim(), phone: f.phone.trim(),
        address: f.address.trim() || undefined, city: f.city || (picked?.city || undefined),
        wants: f.wants || undefined, preferred_window: (!time && f.window) ? f.window : undefined,
        preferred_day: (!time && f.pday) ? f.pday : undefined,
        referral_code: clean, preferred_contact: 'phone',
      });
      if (day && time && r.data?.lead_id && r.data?.book_token) {
        try {
          const b = await publicApi.post('/portal/quote-visit', { lead_id: r.data.lead_id, book_token: r.data.book_token, date: day, time });
          setSent({ ...r.data, booked: b.data });
        } catch (e: any) {
          // The form is saved either way; only the time needs another pick.
          if (e?.response?.data?.taken) { setLeadSaved(r.data); setProblem('That time just went. Pick another and tap Book.'); setTime(''); refetchSlots(); return; }
          setSent({ ...r.data, booked: null, bookFailed: e?.response?.data?.message || 'could not book the time' });
        }
        return;
      }
      setSent(r.data);
    } catch (e: any) {
      setProblem(e?.response?.data?.message || `That did not send. Text us at ${co.phone} and we will sort it out.`);
    } finally { setSending(false); }
  }

  const smsBody = encodeURIComponent(`Hi, ${first} sent me with code ${clean}. I would like a mowing quote for [your address].`);

  if (isLoading) return <PublicLoading />;
  if (isError) return <DeadLink what="referral" company={co} />;

  if (sent) {
    return (
      <PublicPage company={co} context={`Code ${clean}`}>
        <Panel tone="good" className="text-center">
          <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-3" aria-hidden />
          <H1>{sent.booked ? `You're booked, ${f.first_name.trim()}` : `Got it, ${f.first_name.trim()}`}</H1>
          <p className="text-[15px] text-gray-900 mt-3">
            {sent.booked ? (
              <><span className="font-bold">{sent.booked.when}</span>. Michael comes by the yard and texts you before he heads over.</>
            ) : (
              <>Michael will text you{f.window ? ` in the ${f.window}` : ' shortly'}{sent.bookFailed ? ' to set a time' : ''}.</>
            )}
          </p>
          <p className="text-[15px] text-gray-900 mt-2">
            Your code <span className="font-black tracking-wider">{clean}</span> is on your quote, so you and {first} both
            get the half-price mow after your second visit.
          </p>
        </Panel>
        <ButtonGhost href={`sms:${co.phone_raw}`}>
          <MessageSquare className="h-5 w-5" aria-hidden /> Need us sooner? Text {co.phone}
        </ButtonGhost>
      </PublicPage>
    );
  }

  return (
    <PublicPage company={co} context={`Code ${clean}`}>
      <section className="bg-white rounded-3xl border border-brand-100 shadow-lift overflow-hidden">
        <div className="px-5 pt-5 pb-5">
          <p className="text-[13px] font-black uppercase tracking-wide text-brand-600">A neighbor sent you</p>
          <H1 className="mt-1">{first} sent you</H1>
          <p className="text-[15px] text-gray-800 mt-2">
            Tell us where the yard is and we will text you a price. It takes a minute.
          </p>
        </div>
        <div className="px-5 py-6 bg-brand-600 text-black">
          <p className="text-[13px] font-black uppercase tracking-wide text-black/70">Your code</p>
          <p className="text-3xl font-black tracking-[0.15em] mt-1">{clean}</p>
          <p className="text-[15px] font-medium text-black/85 mt-2">
            It is already on your quote. You and {first} both get a half-price mow after your second visit,
            and 5% off a project over $500.
          </p>
        </div>
      </section>

      <Panel title="Where is the yard?">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={LABEL}>First name</span>
              <input value={f.first_name} onChange={set('first_name')} className={FIELD} placeholder="Jordan" autoCapitalize="words" autoComplete="given-name" />
            </label>
            <label className="block">
              <span className={LABEL}>Last name</span>
              <input value={f.last_name} onChange={set('last_name')} className={FIELD} placeholder="Lee" autoCapitalize="words" autoComplete="family-name" />
            </label>
          </div>
          <label className="block">
            <span className={LABEL}>Mobile <span className="font-normal text-gray-700">(we text, we don't call)</span></span>
            <input value={f.phone} onChange={set('phone')} className={FIELD} placeholder="(512) 555-0100" type="tel" inputMode="tel" autoComplete="tel" />
          </label>
          <div>
            <span className={LABEL}>Address of the yard</span>
            <AddressAutocomplete
              api={publicApi}
              suggestPath="/portal/address-suggest"
              placePath="/portal/address-place"
              value={f.address}
              onChange={v => { setF(p => ({ ...p, address: v })); setPicked(null); }}
              onSelect={s => { setF(p => ({ ...p, address: s.address || s.label, city: s.city || p.city, state: s.state || 'TX', zip: s.zip || p.zip })); setPicked(s); }}
              placeholder="Start typing your street"
            />
            {picked && <p className="mt-1.5 text-[14px] font-semibold text-emerald-700 flex items-center gap-1"><CheckCircle className="h-4 w-4" aria-hidden /> {picked.label}</p>}
          </div>
          <div>
            <span className={LABEL}>What are you after?</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {WANTS.map(w => (
                <button key={w.key} type="button" aria-pressed={f.wants === w.key}
                  onClick={() => setF(p => ({ ...p, wants: p.wants === w.key ? '' : w.key }))} className={chip(f.wants === w.key)}>{w.label}</button>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      <Panel title={slots?.days?.length > 0 && !justTime ? 'When can Michael come by?' : 'Best time to reach you'}>
        {slots?.days?.length > 0 && !justTime ? (
          <>
            <p className="text-[15px] text-gray-800 -mt-2 mb-3">A {slots.minutes}-minute look at the yard. These are the times he has open.</p>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {slots.days.map((d: any) => (
                <button key={d.date} type="button" aria-pressed={day === d.date} onClick={() => { setDay(d.date); setTime(''); }}
                  className={`press flex-shrink-0 px-4 min-h-[62px] rounded-2xl border-2 text-center ${day === d.date ? 'bg-brand-600 text-black border-brand-600 shadow-brand' : 'bg-white text-gray-900 border-brand-200 shadow-soft'}`}>
                  <span className="block text-[15px] font-black">{d.label.split(',')[0].slice(0, 3)}</span>
                  <span className="block text-[13px] opacity-90">{d.label.split(', ')[1]}</span>
                </button>
              ))}
            </div>
            {dayRow && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {dayRow.times.map((t: string, i: number) => (
                  <button key={t} type="button" aria-pressed={time === t} onClick={() => setTime(t)}
                    className={`press min-h-[54px] rounded-2xl border-2 text-[15px] font-bold ${time === t ? 'bg-brand-600 text-black border-brand-600 shadow-brand' : 'bg-white text-gray-900 border-brand-200 shadow-soft'}`}>
                    {dayRow.labels[i]}
                  </button>
                ))}
              </div>
            )}
            {day && time && (
              <p className="mt-3 text-[15px] font-semibold text-emerald-700 flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4" aria-hidden /> {dayRow?.label} at {dayRow?.labels[dayRow.times.indexOf(time)]}
              </p>
            )}
            <button type="button" onClick={() => { setJustTime(true); setDay(''); setTime(''); }}
              className="press mt-3 text-[14px] font-bold text-brand-700 underline min-h-[48px]">None of these work</button>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {WINDOWS.map(w => (
                <button key={w.key} type="button" aria-pressed={f.window === w.key}
                  onClick={() => setF(p => ({ ...p, window: p.window === w.key ? '' : w.key }))} className={chip(f.window === w.key)}>{w.label}</button>
              ))}
            </div>
            <p className="text-[14px] font-bold text-gray-900 mt-3 mb-1">A day that suits you? <span className="font-normal text-gray-500">(optional)</span></p>
            <div className="flex flex-wrap gap-2">
              {DAYS.map(d => (
                <button key={d} type="button" aria-pressed={f.pday === d}
                  onClick={() => setF(p => ({ ...p, pday: p.pday === d ? '' : d }))} className={chip(f.pday === d)}>{d.slice(0, 3)}</button>
              ))}
            </div>
            {slots?.days?.length > 0 && (
              <button type="button" onClick={() => setJustTime(false)}
                className="press mt-3 text-[14px] font-bold text-brand-700 underline min-h-[48px]">Pick an exact time instead</button>
            )}
          </>
        )}
      </Panel>

      {problem && <Panel tone="warn"><p className="text-[15px] font-bold text-gray-900">{problem}</p></Panel>}

      <div className="space-y-2">
        {leadSaved ? (
          <ButtonPrimary onClick={bookAgain} disabled={sending || !time}>{sending ? 'Booking' : 'Book this time'}</ButtonPrimary>
        ) : (
          <ButtonPrimary onClick={send} disabled={sending}>{sending ? 'Sending' : day && time ? 'Book it' : 'Send it to Michael'}</ButtonPrimary>
        )}
        <p className="text-[14px] text-gray-700 text-center">
          {day && time ? 'Nothing is charged. Michael texts you before he comes by.' : 'Nothing is booked or charged. Michael texts you a price first.'}
        </p>
        <ButtonGhost href={`sms:${co.phone_raw}?&body=${smsBody}`}>
          <Phone className="h-5 w-5" aria-hidden /> Rather text? Tap here
        </ButtonGhost>
      </div>
    </PublicPage>
  );
}
